<script setup lang="ts">
// Shared frame for the Today page's cards: a tone dot naming the pillar,
// a heading, an optional "see all" link and a body slot. Keeps the four
// cards visually identical so only their content differs.
export type TodayTone = 'attention' | 'factory' | 'life' | 'brain'

defineProps<{
  title: string
  tone: TodayTone
  linkTo?: string
  linkLabel?: string
  testid: string
}>()

defineSlots<{ default: () => unknown }>()

const TONE_DOT: Record<TodayTone, string> = {
  attention: 'bg-attention',
  factory: 'bg-factory',
  life: 'bg-life',
  brain: 'bg-brand-primary',
}

const TONE_LINK: Record<TodayTone, string> = {
  attention: 'text-attention',
  factory: 'text-factory',
  life: 'text-life',
  brain: 'text-brand-primary',
}
</script>

<template>
  <section
    class="flex flex-col rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
    :data-testid="testid"
    :aria-labelledby="`${testid}-heading`"
  >
    <div class="mb-2 flex items-center gap-2">
      <span class="h-2 w-2 rounded-full" :class="TONE_DOT[tone]" aria-hidden="true" />
      <h2 :id="`${testid}-heading`" class="m-0 flex-1 text-[15px] font-semibold text-ink">{{ title }}</h2>
      <RouterLink
        v-if="linkTo"
        :to="linkTo"
        class="text-[13px] underline underline-offset-2"
        :class="TONE_LINK[tone]"
        :data-testid="`${testid}-link`"
        >{{ linkLabel }}</RouterLink
      >
    </div>
    <slot />
  </section>
</template>
